import { SphereDropdownSelector } from "@components/common/dropdown/sphere-dropdown-selector";
import { ExportAllDataDialog } from "@components/common/export/export-all-data-dialog";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useLoadingState } from "@context-providers/loading-state-context";
import { blue, FaroTextVariant } from "@flat-ui";
import { useToast } from "@hooks/use-toast";
import { Skeleton } from "@mui/material";
import { useAppDispatch } from "@store/store-helper";
import { setBulkActionName } from "@store/table/table-slice";
import { ExportFormat } from "@utils/file-data-export";
import { ExportType, TableType } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useState, useRef, useCallback } from "react";
import { ExportHandler, ExportHandlerOptions } from "@components/common/export/export-dropdown-types";

/** Label to display for each available export format */
const EXPORT_FORMAT_LABEL: Record<ExportFormat, string> = {
  CSV: ".csv",
  XLSX: ".xlsx (Excel)",
  JSON: ".json",
};

interface Props {
  /** The prop name sent for tracking */
  trackingProps: {
    dataType?: TableType;
    numberOfEntities: number;
    areEntitiesSelected: boolean;
  };

  /**
   * Handler to export data in the given format.
   * When no entities are selected (export all), the handler receives
   * `onProgress` callback and `signal` for cancellation support.
   */
  exportHandler: ExportHandler;

  /** Message to show when the export is successful or with error */
  toastMessage: {
    /** Success message shown as toast */
    success: string;

    /** Error message shown as toast */
    error: string;
  };

  /** Text to show when user hover over the button */
  buttonTooltipText?: string;

  /**
   * Whether to show a loading skeleton instead of the dropdown.
   * If not provided, will automatically use loading state from LoadingStateProvider context if available.
   */
  shouldShowSkeleton?: boolean;
}

/** Dropdown to export data in the file format selected by the user */
export function ExportDropdown({
  trackingProps,
  exportHandler,
  toastMessage,
  buttonTooltipText = "Export",
  shouldShowSkeleton,
}: Props): JSX.Element {
  const { showToast } = useToast();
  const dispatch = useAppDispatch();
  const { trackEvent } = useTrackEvent();
  const { handleErrorWithToast } = useErrorContext();
  const loadingStateContext = useLoadingState();
  const [isExportAllDialogOpen, setIsExportAllDialogOpen] = useState(false);
  const [currentPageNumber, setCurrentPageNumber] = useState<number>(0);
  const [currentFormat, setCurrentFormat] = useState<ExportFormat | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Use explicit prop if provided, otherwise fall back to context, otherwise default to false
  const isLoading =
    shouldShowSkeleton ?? loadingStateContext?.isLoading ?? false;

  /** Handles cancel action from the export all progress dialog */
  const handleCancel = useCallback((): void => {
    if (currentFormat) {
      trackEvent({
        name: ExportType.exportTableCancelled,
        props: {
          ...trackingProps,
          format: currentFormat,
          exportType: "all",
        },
      });
    }
    abortControllerRef.current?.abort();
    setIsExportAllDialogOpen(false);
    setCurrentFormat(null);
    setCurrentPageNumber(0);
  }, [currentFormat, trackEvent, trackingProps]);

  /** Sets up UI state for export operation */
  const setupExportUI = useCallback(
    (isExportAll: boolean, format: ExportFormat): void => {
      if (isExportAll) {
        setCurrentFormat(format);
        setCurrentPageNumber(0);
        setIsExportAllDialogOpen(true);
      } else {
        dispatch(setBulkActionName("export"));
      }
    },
    [dispatch]
  );

  /**
   * Export handler that supports both selected and all export types.
   * @param format Format to export the data as (CSV, XLSX, JSON)
   */
  const handleExport = useCallback(
    async (format: ExportFormat): Promise<void> => {
      const isExportAll = !trackingProps.areEntitiesSelected;
      const exportType = isExportAll ? "all" : "selected";

      const amplitudeProps = {
        ...trackingProps,
        format,
        exportType,
        areEntitiesSelected: exportType === "selected",
      };
  
      // Track export started event
      trackEvent({
        name: ExportType.exportTableStarted,
        props: amplitudeProps,
      });

      setupExportUI(isExportAll, format);

      // Create abort controller for this export
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      try {
        // Build options for export handler
        const options: ExportHandlerOptions | undefined = isExportAll
          ? {
              onProgress: (currentPage: number) => {
                setCurrentPageNumber(currentPage);
              },
              signal: abortController.signal,
            }
          : undefined;

        const result = await exportHandler(format, options);

        // If cancelled, don't show success toast
        if (result === null) {
          return;
        }

        // Track export success event
        trackEvent({
          name: ExportType.exportTable,
          props: {
            ...amplitudeProps,
            numberOfEntities: result,
          },
        });
        showToast({
          type: "success",
          message: toastMessage.success,
        });
      } catch (error) {
        const errorId = `handleExport-${format}-${Date.now()}`;
        trackEvent({
          name: ExportType.exportTableFailed,
          props: {
            ...amplitudeProps,
            errorMessage: toastMessage.error,
            errorId,
          },
        });

        handleErrorWithToast({
          id: errorId,
          title: toastMessage.error,
          error,
        });
      } finally {
        abortControllerRef.current = null;
        if (isExportAll) {
          setIsExportAllDialogOpen(false);
          setCurrentFormat(null);
          setCurrentPageNumber(0);
        } else {
          dispatch(setBulkActionName(null));
        }
      }
    },
    [
      dispatch,
      exportHandler,
      handleErrorWithToast,
      showToast,
      toastMessage.error,
      toastMessage.success,
      trackEvent,
      trackingProps,
      setupExportUI,
    ]
  );

  if (isLoading) {
    return (
      <Skeleton variant="rectangular" sx={{ width: "80px", height: "24px" }} />
    );
  }

  return (
    <>
      <SphereDropdownSelector<ExportFormat>
        items={[
          { value: "CSV", label: EXPORT_FORMAT_LABEL.CSV },
          { value: "XLSX", label: EXPORT_FORMAT_LABEL.XLSX },
          { value: "JSON", label: EXPORT_FORMAT_LABEL.JSON },
      ]}
        defaultLabel="Export"
        buttonTooltipText={buttonTooltipText}
        handleChange={handleExport}
        variant={FaroTextVariant.buttonS}
        iconColor={blue[500]}
      />

      <ExportAllDataDialog
        isOpen={isExportAllDialogOpen}
        currentPageNumber={currentPageNumber}
        onCancel={handleCancel}
      />
    </>
  );
}
